import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './auth';
import { LanguageProvider } from './i18n';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './ErrorBoundary';

import Layout from './Layout';
import ProtectedRoute from './ProtectedRoute';

// Public marketing pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import BrandsPage from './pages/BrandsPage';
import LavashPage from './pages/LavashPage';
import VeloraPage from './pages/VeloraPage';
import ContactsPage from './pages/ContactsPage';

// Legal & payments (public)
import LegalPageView from './pages/LegalPageView';
import { PaymentSuccessPage, PaymentFailPage } from './pages/PaymentPages';

// Auth
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

// App pages
import AdminDashboard from './pages/AdminDashboard';
import ClientDashboard from './pages/ClientDashboard';
import OperationsDashboard from './pages/OperationsDashboard';
import NewOrderPage from './pages/NewOrderPage';
import OrdersListPage from './pages/OrdersListPage';
import CompaniesPage from './pages/CompaniesPage';
import ProductsPage from './pages/ProductsPage';
import CategoriesPage from './pages/CategoriesPage';
import UsersPage from './pages/UsersPage';
import RepeatOrderPage from './pages/RepeatOrderPage';
import BalancePage from './pages/BalancePage';
import DocumentsPage from './pages/DocumentsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SegmentsPage from './pages/SegmentsPage';
import CRMPage from './pages/CRMPage';
import LoyaltyPage from './pages/LoyaltyPage';
import PromoCodesPage from './pages/PromoCodesPage';
import IngredientsPage from './pages/IngredientsPage';
import EmployeesPage from './pages/EmployeesPage';
import SMSPage from './pages/SMSPage';
import PendingApprovalsPage from './pages/PendingApprovalsPage';
import InvitePage from './pages/InvitePage';
import SettingsPage from './pages/SettingsPage';
import LegalEditorPage from './pages/LegalEditorPage';

import './App.css';

const Protected = ({ children, roles }) => (
  <ProtectedRoute allowedRoles={roles}><Layout>{children}</Layout></ProtectedRoute>
);

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              {/* Public marketing */}
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/brands" element={<BrandsPage />} />
              <Route path="/lavash" element={<LavashPage />} />
              <Route path="/velora" element={<VeloraPage />} />
              <Route path="/contacts" element={<ContactsPage />} />

              {/* Legal */}
              <Route path="/terms" element={<LegalPageView />} />
              <Route path="/privacy" element={<LegalPageView />} />
              <Route path="/refund-policy" element={<LegalPageView />} />

              {/* Payments */}
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/payment/fail" element={<PaymentFailPage />} />

              {/* Auth */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />

              {/* Admin */}
              <Route path="/admin" element={<Protected roles={['super_admin','admin']}><AdminDashboard /></Protected>} />
              <Route path="/admin/orders" element={<Protected roles={['super_admin','admin']}><OrdersListPage /></Protected>} />
              <Route path="/admin/companies" element={<Protected roles={['super_admin','admin']}><CompaniesPage /></Protected>} />
              <Route path="/admin/products" element={<Protected roles={['super_admin','admin']}><ProductsPage /></Protected>} />
              <Route path="/admin/categories" element={<Protected roles={['super_admin','admin']}><CategoriesPage /></Protected>} />
              <Route path="/admin/users" element={<Protected roles={['super_admin','admin']}><UsersPage /></Protected>} />
              <Route path="/admin/analytics" element={<Protected roles={['super_admin','admin']}><AnalyticsPage /></Protected>} />
              <Route path="/admin/segments" element={<Protected roles={['super_admin','admin']}><SegmentsPage /></Protected>} />
              <Route path="/admin/crm" element={<Protected roles={['super_admin','admin']}><CRMPage /></Protected>} />
              <Route path="/admin/loyalty" element={<Protected roles={['super_admin','admin']}><LoyaltyPage /></Protected>} />
              <Route path="/admin/promo-codes" element={<Protected roles={['super_admin','admin']}><PromoCodesPage /></Protected>} />
              <Route path="/admin/ingredients" element={<Protected roles={['super_admin','admin']}><IngredientsPage /></Protected>} />
              <Route path="/admin/employees" element={<Protected roles={['super_admin','admin']}><EmployeesPage /></Protected>} />
              <Route path="/admin/sms" element={<Protected roles={['super_admin','admin']}><SMSPage /></Protected>} />
              <Route path="/admin/pending" element={<Protected roles={['super_admin','admin']}><PendingApprovalsPage /></Protected>} />
              <Route path="/admin/invite" element={<Protected roles={['super_admin','admin']}><InvitePage /></Protected>} />
              <Route path="/admin/settings" element={<Protected roles={['super_admin','admin']}><SettingsPage /></Protected>} />
              <Route path="/admin/legal" element={<Protected roles={['super_admin','admin']}><LegalEditorPage /></Protected>} />

              {/* Ops */}
              <Route path="/operations" element={<Protected roles={['operations_manager']}><OperationsDashboard /></Protected>} />
              <Route path="/operations/orders" element={<Protected roles={['operations_manager']}><OrdersListPage /></Protected>} />

              {/* Client */}
              <Route path="/client" element={<Protected roles={['client_user']}><ClientDashboard /></Protected>} />
              <Route path="/client/new-order" element={<Protected roles={['client_user']}><NewOrderPage /></Protected>} />
              <Route path="/client/orders" element={<Protected roles={['client_user']}><OrdersListPage /></Protected>} />
              <Route path="/client/repeat" element={<Protected roles={['client_user']}><RepeatOrderPage /></Protected>} />
              <Route path="/client/documents" element={<Protected roles={['client_user']}><DocumentsPage /></Protected>} />
              <Route path="/client/balance" element={<Protected roles={['client_user']}><BalancePage /></Protected>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
